import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { animatedStyle } from '@css/styles';
import {
  darkBlue,
  darkerBlue,
  green,
  gray,
  sunfireGreen,
  lochmaraBlue,
  red,
  white,
  marketingMobileCuttoff,
  lightGray,
} from '@css/constants';
import { FontNormal } from '@css/fonts';
import { fontSize } from "@css/styles";
import { Props } from './Button';

const focusPaddingSize = '1px';

export const BaseButton = styled('button')<Props>(props => css`
  -webkit-appearance: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  ${animatedStyle}
  ${FontNormal(600)}
  ${props.fixedFont ? 'font-size: 16px' : fontSize(16)};
  appearance: none;
  border: 1px solid transparent;
  border-radius: ${props.circled ? '50% !important' : 'inherit'};
  box-shadow: 0px 4px 12px rgba(26, 28, 31, 0.25);
  cursor: pointer;
  display: ${props.circled ? 'flex' : 'inline-block'};
  font-family: inherit;
  outline: none;
  padding: 18px 22px 16px;
  position: relative;
  text-align: center;
  text-decoration: none;
  touch-action: manipulation;
  transition: all 0.2s ease;
  user-select: none;
  white-space: nowrap;
  width: ${props.circled ? '56px' : 'inherit'};
  height: ${props.circled ? '56px' : 'inherit'};
  align-items: ${props.circled ? 'center' : 'inherit'};
  justify-content: ${props.circled ? 'center' : 'inherit'};

  &:hover {
    border-color: transparent;
    text-decoration: none;
  }

  &:active {
    opacity: 0.8;
  }

  &[disabled] {
    background: ${gray};
  }

  &[disabled]:hover {
    background: ${gray};
  }

  &:focus::before {
    content: "";
    box-shadow: 0 0 0 ${focusPaddingSize} ${darkBlue};
    padding: ${focusPaddingSize};
    pointer-events: none;
    position: absolute;
    left: 0;
    top: 0;
    border-radius: ${props.circled ? '50% !important' : 'inherit'};
    height: ${props.circled ? 'calc(100% + 2px)' : `calc(100% + ${focusPaddingSize} * 2)`};
    width: ${props.circled ? 'calc(100% + 2px)' : `calc(100% + ${focusPaddingSize} * 2)`};
    margin-top: ${props.circled ? '-2px' : `calc(${focusPaddingSize} * -2)`};
    margin-left: ${props.circled ? '-2px' : `calc(${focusPaddingSize} * -2)`};
  }
`);

export const FilledButton = styled(BaseButton)(css`
  ${FontNormal(600)}
  background-color: ${darkBlue};
  border-radius: 2px;
  border: 0;
  color: ${white};
  margin: 0 auto;
  text-transform: uppercase;

  &:hover {
    color: ${white};
    background-color: ${darkerBlue};
  }

  &:active,
  &:visited,
  &:focus {
    color: ${white};
  }
`);

export const TextButton = styled(BaseButton)(css`
  background-color: transparent;
  color: ${darkBlue};
  display: flex;
  padding: 8px 4px;
  text-transform: uppercase;
  box-shadow: none;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
    color: ${darkerBlue};
  }

  &:active,
  &:visited,
  &:focus {
    color: ${darkBlue};
    box-shadow: none;
  }

  &:focus::before {
    border-radius: 2px;
  }

  &[disabled],
  &[disabled]:hover {
    background: transparent;
    opacity: 0.5;
  }
`);

export const OutlinedButton = styled(BaseButton)(css`
  background: ${white};
  border-radius: 2px;
  border: 1px solid ${darkBlue};
  color: ${darkBlue};
  text-transform: uppercase;

  &:hover {
    background-color: #f9f9f9;
    border-color: ${darkBlue};
    color: ${darkerBlue};
  }

  &[disabled],
  &[disabled]:hover {
    background: ${white};
    opacity: 0.5;
  }
`);

export const GreenButton = styled(FilledButton)(css`
  background-color: ${sunfireGreen};

  &:hover {
    background-color: ${green};
  }
`)

export const WhiteButton = styled(FilledButton)(css`
  background: ${white};
  color: ${green};
  padding: 18px 40px 16px;

  @media(max-width: ${marketingMobileCuttoff}) {
    padding: 18px 20px 16px;
  }

  &:hover {
    background-color: ${lightGray};
    color: ${green};
  }

  &:active,
  &:visited,
  &:focus {
    color: ${green};
  }
`)

export const BlueButton = styled(FilledButton)(css`
  background: ${white};
  color: ${darkBlue};
  padding: 18px 40px 16px;
  @media(max-width: ${marketingMobileCuttoff}) {
    padding: 18px 20px 16px;
  }
  &:hover {
    background-color: ${lightGray};
    color: ${darkBlue};
  }
  &:active,
  &:visited,
  &:focus {
    color: ${darkBlue};
  }
`)

export const WhiteButtonBlueText = styled(WhiteButton)(css`
  color: ${darkBlue};
  padding: 18px 100px 16px;

  &:hover {
    color: ${darkBlue};
  }

  &:active,
  &:visited,
  &:focus {
    color: ${darkBlue};
  }
`)

export const GreenButtonWide = styled(GreenButton)(css`
  padding-left: 45px;
  padding-right: 45px;
`)

export const GreenOutlinedButton = styled(OutlinedButton)(css`
  border-color: ${sunfireGreen};
  color: ${sunfireGreen};

  &:hover {
    border-color: ${green};
    color: ${green};
  }
`);

export const LinkButton = styled(BaseButton)<Props>(props => css`
  ${FontNormal(500)};
  ${fontSize(16)};
  ${props.fixedFont ? 'font-size: 16px' : fontSize(16)};
  box-shadow: none;
  color: ${lochmaraBlue};
  cursor: pointer;
  margin: 0;
  padding: 0;
  text-decoration: underline;
  background: transparent;

  &:hover {
    color: ${darkBlue};
  }

  &:focus::before {
    content: "";
    border-bottom: 1px solid currentcolor;
    box-shadow: none;
    height: 0;
    pointer-events: none;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    top: 1.2rem;
  }

  &:active {
    background: transparent;
  }

  &[disabled],
  &[disabled]:hover {
    background: transparent;
    opacity: 0.5;
  }
`);

export const LinkWhiteButton = styled(LinkButton)(css`
  ${FontNormal(600)};
  color: ${white};

  &:hover {
    color: ${white};
  }

  &:focus::before {
    border-bottom: none;
  }
`)

export const LinkWhiteHoverUnderlineButton = styled(LinkWhiteButton)(css`
  text-decoration: none;
  
  &:hover {
    color: ${white};
    text-decoration: underline;
  }
`)

export const RedDot = styled('div')(css`
  z-index: 10;
  position: absolute;
  background: ${red};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  top: -5px;
  right: -5px;
`)
